import './App.css';

import { BiometricSetupConfirmModal } from '@components/BiometricSetupDrawer/BiometricSetupConfirmModal.component';
import { BiometricSetupDrawer } from '@components/BiometricSetupDrawer/BiometricSetupDrawer.component';
import { NetworkStatusNotifier } from '@components/NetworkStatus/NetworkStatusNotifier.component';
import { PromptToUpdateToNewVersion } from '@components/PromptToUpdateToNewVersion/PromptToUpdateToNewVersion.component';
import { AccessDenied } from '@components/UI/AccessDenied/AccessDenied.component';
import { MainLoadingView } from '@components/UI/MainLoadingView/MainLoadingView.component';
import { Snackbar } from '@components/UI/Snackbar/Snackbar.component';
import { API_ENDPOINT } from '@shared/constants/api-endpoints';
import { ROUTE_PATH } from '@shared/constants/route-path.enum';
import { useBiometricAuth } from '@shared/hooks/biometrics/useBiometricAuth';
import { useBiometricColdStart } from '@shared/hooks/biometrics/useBiometricColdStart';
import { useBiometricSetup } from '@shared/hooks/biometrics/useBiometricSetup';
import { useAppInfo } from '@shared/hooks/useAppInfo';
import { useAppStateListener } from '@shared/hooks/useAppStateListener';
import { useFetch } from '@shared/hooks/useFetch';
import { useInitiateSummaryAndMenu } from '@shared/hooks/useInitiateSummaryAndMenu';
import { useKeycloakAuth } from '@shared/hooks/useKeycloakAuth';
import useMobileDetector from '@shared/hooks/useMobileDetector';
import { usePushNotifications } from '@shared/hooks/usePushNotifications';
import { PartyInfoDTO } from '@shared/models/PartyInfoDTO.model';
import { UserPreferencesDTO } from '@shared/models/UserPreferencesDTO.model';
import { USER_TYPE } from '@shared/models/UserType.model';
import { AppActions } from '@shared/store/app/app';
import {
  selectIsMobileVersionCompatible,
  selectIsThereANewerMobileVersionAvailable,
  selectUserInfo
} from '@shared/store/app/app.selectors';
import { selectAuth } from '@shared/store/auth/auth.selectors';
import { UIActions } from '@shared/store/ui/ui';
import { ViewportActions } from '@shared/store/viewport/viewport';
import { MdcTheme } from 'ids-wc/dist/react/components';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { Routing } from './routing/Routing.tsx';

export type AppStatus = 'loading' | 'ok' | 'error' | 'missing_afm' | 'unregistered_immigrant' | 'mobileRequiresUpdate';

function App() {
  const { logout } = useKeycloakAuth(); // We need to keep it, to initialize the Keycloak instance
  usePushNotifications();

  const dispatch = useDispatch();
  const isMobile = useMobileDetector();
  const [status, setStatus] = useState<AppStatus>('loading');
  const [showBiometricConfirmationModal, setShowBiometricConfirmationModal] = useState<boolean>(false);
  const { initiateSummaryAndMenu } = useInitiateSummaryAndMenu();
  const userInfo = useSelector(selectUserInfo);

  // Biometric authentication hooks
  const { isBiometricAvailable, enableBiometrics } = useBiometricAuth();
  const { isWaitingForBiometricAuth } = useBiometricColdStart(logout);

  const { handleBiometricAccept, showBiometricSetup, handleBiometricDecline } = useBiometricSetup(
    status,
    !!isBiometricAvailable,
    enableBiometrics
  );

  const auth = useSelector(selectAuth);
  const { fetchAppInfo } = useAppInfo();
  const isThereANewerMobileVersionAvailable = useSelector(selectIsThereANewerMobileVersionAvailable);
  const isMobileVersionCompatible = useSelector(selectIsMobileVersionCompatible);

  const onApproveBiometrics = () => {
    handleBiometricAccept().then(() => setShowBiometricConfirmationModal(true));
  };

  // Handle app resume from background (NOT for cold start)
  const handleAppBecomeActive = useCallback(() => {
    if (auth.isAuthenticated) {
      fetchAppInfo();
      // Don't prompt biometrics on resume from background
      console.log('[App] App resumed from background');
    }
  }, [auth.isAuthenticated, fetchAppInfo]);

  // Listen for app state changes (background -> foreground)
  useAppStateListener(handleAppBecomeActive);

  const { get: getPreferences } = useFetch<UserPreferencesDTO>(API_ENDPOINT.USER_PREFERENCES);
  const { get: getAMKAEMAESUserData } = useFetch<PartyInfoDTO>(API_ENDPOINT.INSURED_WITH_INTEGRATIONS_REGISTRATION);
  const { get: getUserData } = useFetch<PartyInfoDTO>(API_ENDPOINT.INSURED_FROM_PERSEAS);
  const { get: getInboxStatus } = useFetch<{ hasUnread: boolean }>(API_ENDPOINT.NOTIFICATIONS_INBOX_STATUS);

  useEffect(() => {
    dispatch(ViewportActions.updateViewport({ width: window.innerWidth }));
  }, [dispatch, isMobile]);

  const hasMissingAFM = (data: PartyInfoDTO) => {
    return data.identifications.some((x) => x.identifierType === 'AFM' && x.identifierValue === null);
  };

  const isImmigrant = (data: PartyInfoDTO) => {
    const GR_CITIZEN_NATIONALITY_CODE = 'GR';
    return data.placeOfOrigin?.nationality?.code !== GR_CITIZEN_NATIONALITY_CODE;
  };

  const handleSuccessfulUserFetch = useCallback(
    (data: PartyInfoDTO) => {
      if (hasMissingAFM(data)) {
        setStatus('missing_afm');
        return;
      }

      dispatch(AppActions.setUser({ user: data }));
      setStatus('ok');
    },
    [dispatch]
  );

  const onFailedUserFetch = (error: unknown) => {
    console.error('Failed to fetch user data:', error);
    setStatus('error');
  };

  const fetchAMKAEMAESForUnregistered = useCallback(async () => {
    try {
      const { data } = await getAMKAEMAESUserData({ shouldAbort: false });

      if (isImmigrant(data)) {
        // insured is NOT registered and its immigrant
        setStatus('unregistered_immigrant');
        return;
      }

      handleSuccessfulUserFetch(data);
      dispatch(UIActions.openInsuredCreateForm());
    } catch (error) {
      onFailedUserFetch(error);
    }
  }, [dispatch, getAMKAEMAESUserData, handleSuccessfulUserFetch]);

  const fetchUserData = useCallback(async () => {
    try {
      const { data } = await getUserData({ shouldAbort: false });
      const isRegistered = !!Object.keys(data).length;

      if (!isRegistered) {
        // If the user has no data, we need to fetch the updated insured data from the
        // AMKA/EMAES service and automatically navigate the user to registration flow (ΑΠΟΓΡΑΦΗ)
        return fetchAMKAEMAESForUnregistered();
      }

      if (hasMissingAFM(data)) {
        setStatus('missing_afm');
        return;
      }

      // Fetch all data in parallel to avoid sequential re-renders
      const [preferencesRes, inboxRes] = await Promise.allSettled([
        getPreferences({ shouldAbort: false }),
        getInboxStatus({ shouldAbort: false })
      ]);

      // Batch all state updates together to minimize re-renders
      dispatch(AppActions.setUser({ user: data }));

      if (preferencesRes.status === 'fulfilled') {
        dispatch(AppActions.setUserPreferences(preferencesRes.value.data));
      }

      if (inboxRes.status === 'fulfilled') {
        dispatch(AppActions.setInboxStatus(inboxRes.value.data));
      }

      setStatus('ok'); // This will be batched with the dispatch calls above
    } catch (error) {
      onFailedUserFetch(error);
    }
  }, [fetchAMKAEMAESForUnregistered, getUserData, dispatch, getPreferences, getInboxStatus]);

  useEffect(() => {
    const runSequentially = async () => {
      const amka = auth.tokenParsed?.efka_access?.amka;

      if (auth.isAuthenticated && !amka) {
        console.error('No AMKA found in token:', auth.tokenParsed);
        setStatus('error');
        return;
      }

      if (auth.isAuthenticated) {
        await fetchUserData();
        fetchAppInfo();
      }
    };

    runSequentially();
  }, [fetchUserData, auth.isAuthenticated, auth.tokenParsed, fetchAppInfo]);

  useEffect(() => {
    if (userInfo) {
      initiateSummaryAndMenu();
    }
  }, [initiateSummaryAndMenu, userInfo]);

  const statusContent = useMemo(() => {
    // Show loading while waiting for biometric authentication
    if (isWaitingForBiometricAuth) {
      return <MainLoadingView />;
    }

    if (status === 'ok' && !isMobileVersionCompatible) {
      console.info('Incompatible mobile version detected, showing update required screen');
      return <AccessDenied reason="mobileRequiresUpdate" />;
    }

    switch (status) {
      case 'loading':
        return <MainLoadingView />;
      case 'ok':
        return (
          <Router>
            <Routing />
          </Router>
        );
      case 'error':
        return <AccessDenied reason="generic" />;
      case 'missing_afm':
        return <AccessDenied reason="missingAFM" />;
      case 'unregistered_immigrant':
        return <AccessDenied reason="unregisteredImmigrants" />;
      default:
        return null;
    }
  }, [status, isMobileVersionCompatible, isWaitingForBiometricAuth]);

  if (!auth.isAuthenticated) return null;

  // Redirect non-EXTERNAL_INSURED users to SSP common services home
  if (auth.isAuthenticated && auth?.userType !== USER_TYPE.EXTERNAL_INSURED) {
    window.location.href = `${import.meta.env.VITE_APP_PUBLIC_URL}${ROUTE_PATH.SSP_COMMON_SERVICES_HOME}`;
    return null;
  }

  return (
    <Fragment>
      <MdcTheme />
      <Snackbar />
      <NetworkStatusNotifier />
      {auth.isAuthenticated && statusContent}
      {createPortal(
        <Fragment>
          <BiometricSetupDrawer
            isOpen={showBiometricSetup}
            onAccept={onApproveBiometrics}
            onDecline={handleBiometricDecline}
          />

          <BiometricSetupConfirmModal shouldShow={showBiometricConfirmationModal} />
        </Fragment>,
        document.body
      )}

      {createPortal(<PromptToUpdateToNewVersion isOpen={isThereANewerMobileVersionAvailable} />, document.body)}
    </Fragment>
  );
}

export default App;
